import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProduct,
  getProductSerials,
  getProductSerialsDrafts,
  getProductHistory,
  downloadProductAttachment,
  updateProduct,
  postNewComment,
  presignProductAttachment,
  completeProductAttachment,
  renameProductAttachment,
  deleteProductAttachment,
  MAX_ATTACHMENT_BYTES,
} from "../../../components/products/functions";
import { deleteSerial } from "../../../components/serials/functions";
import { statusObject } from "../../../components/utils/allProductStatus";
import {
  getGradingTypeLabel,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  getVisibleGradeFields,
  renderApplicableGradeValue,
} from "../../../components/utils/serialGradeFields";
import {
  cashnator,
  formatarData,
  formatarDataCompleta,
  cpfMask,
  phoneMask,
} from "../../../components/utils/functions";
import {
  formatProductShipping,
  shippingSharedLabel,
} from "../../../components/utils/formatProductShipping";
import ToastContext from "../../../context/toastProvider";
import MobilePage from "../../ui/MobilePage";
import MobileSection from "../../ui/MobileSection";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileError from "../../ui/MobileError";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileConfirm from "../../ui/MobileConfirm";
import MobileFormField from "../../ui/MobileFormField";
import MobileUploadOverlay from "../../ui/MobileUploadOverlay";
import {
  MobileProductStatusSheet,
  MobileProductEditSheet,
  MobileSerialCreateSheet,
  MobileSerialEditSheet,
} from "./MobileProductDetailSheets";
import { compressImageForUpload } from "./compressImageForUpload";
import { uploadWithProgress } from "../../utils/uploadWithProgress";
import CheckoutGroupPanel from "../../../components/products/CheckoutGroupPanel";

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const actionBtnClass =
  "inline-flex min-h-[36px] items-center rounded-lg border border-primary/50 px-3 text-xs font-medium text-laranjinha/80 hover:bg-monao";

const primaryBtnClass =
  "inline-flex min-h-[36px] items-center rounded-lg bg-primary px-3 text-xs font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";

export default function MobileProductDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);

  const [product, setProduct] = useState(null);
  const [serials, setSerials] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(null);

  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [serialCreateOpen, setSerialCreateOpen] = useState(false);
  const [serialEditOpen, setSerialEditOpen] = useState(false);
  const [editingSerial, setEditingSerial] = useState(null);

  const [newComment, setNewComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  const [uploadName, setUploadName] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadPhase, setUploadPhase] = useState("preparing");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [attachSheetOpen, setAttachSheetOpen] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [renameSheetOpen, setRenameSheetOpen] = useState(false);
  const [renamingAttachment, setRenamingAttachment] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null);
  const [deleteSerialTarget, setDeleteSerialTarget] = useState(null);
  const [restoreHistoryEntry, setRestoreHistoryEntry] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const loadSerials = useCallback(async () => {
    const serialsData = await getProductSerials(user?.token, id);
    const draftsData = await getProductSerialsDrafts(user?.token, id);
    if (serialsData?.type === "success") {
      const drafts =
        draftsData?.type === "success"
          ? draftsData.data?.filter((d) => d?.isDraft) ?? []
          : [];
      setSerials([...(serialsData.data ?? []), ...drafts]);
    }
  }, [user?.token, id]);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError(false);

    const data = await getProduct(user?.token, id);
    if (data?.type !== "success") {
      setError(true);
      setLoading(false);
      return;
    }

    setProduct(data.data);
    setLoading(false);

    await loadSerials();

    const historyData = await getProductHistory(user?.token, id);
    if (historyData?.type === "success") {
      const sorted = [...(historyData.data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setHistory(sorted);
    }
  }, [user?.token, id, loadSerials]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const refreshAfterMutation = async () => {
    const data = await getProduct(user?.token, id);
    if (data?.type === "success") {
      setProduct(data.data);
    }
    await loadSerials();
    const historyData = await getProductHistory(user?.token, id);
    if (historyData?.type === "success") {
      const sorted = [...(historyData.data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setHistory(sorted);
    }
  };

  const handleDownload = async (attachment) => {
    setDownloadBusy(attachment._id);
    try {
      const data = await downloadProductAttachment(
        user?.token,
        product?._id,
        attachment._id
      );
      if (data?.type !== "success" || !data?.downloadUrl) {
        badToast(data?.message ?? "Erro ao baixar anexo");
        return;
      }
      const response = await fetch(data.downloadUrl);
      if (!response.ok) {
        badToast(`Erro ao baixar arquivo (${response.status})`);
        return;
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = attachment?.originalName || attachment?.name || "anexo";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      badToast(err?.message ?? "Erro ao baixar anexo");
    } finally {
      setDownloadBusy(null);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim() || !product?._id) return;
    setCommentBusy(true);
    const data = await postNewComment(user?.token, product._id, newComment.trim());
    setCommentBusy(false);
    if (data?.type !== "success") {
      badToast("Erro ao criar comentário");
      return;
    }
    goodToast("Comentário criado!");
    setNewComment("");
    await refreshAfterMutation();
  };

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAttachSheetOpen(false);
    const displayName = uploadName.trim() || file.name;
    if (!uploadName.trim()) {
      setUploadName(file.name);
    }
    void uploadFile(file, displayName);
  };

  const openAttachSource = (source) => {
    setAttachSheetOpen(false);
    const ref =
      source === "camera"
        ? cameraInputRef
        : source === "video"
          ? videoInputRef
          : galleryInputRef;
    window.setTimeout(() => ref.current?.click(), 50);
  };

  const resetUploadUi = () => {
    setUploadBusy(false);
    setUploadPhase("preparing");
    setUploadPercent(0);
    setUploadFileName("");
  };

  const uploadFile = async (file, name) => {
    if (!product?._id) return;
    setUploadBusy(true);
    setUploadPhase("preparing");
    setUploadPercent(0);
    setUploadFileName(name?.trim() || file.name || "arquivo");

    let toUpload = file;
    try {
      toUpload = await compressImageForUpload(file);
    } catch {
      toUpload = file;
    }

    if (toUpload.size > MAX_ATTACHMENT_BYTES) {
      badToast("Arquivo acima de 50 MB");
      resetUploadUi();
      return;
    }

    const attachmentName = (name.trim() || toUpload.name || file.name).trim();
    if (!attachmentName) {
      badToast("Preencha o nome do anexo");
      resetUploadUi();
      return;
    }
    setUploadFileName(attachmentName);

    const contentType = toUpload.type || "application/octet-stream";
    const presignData = await presignProductAttachment(user?.token, product._id, {
      name: attachmentName,
      contentType,
      size: toUpload.size,
      originalName: toUpload.name || file.name,
    });
    if (presignData?.type !== "success") {
      badToast(presignData?.message ?? "Falha ao preparar upload");
      resetUploadUi();
      return;
    }

    setUploadPhase("uploading");
    setUploadPercent(0);
    try {
      await uploadWithProgress(
        presignData.uploadUrl,
        toUpload,
        contentType,
        ({ percent }) => setUploadPercent(percent)
      );
    } catch (err) {
      badToast(err?.message ?? "Erro de rede no upload");
      resetUploadUi();
      return;
    }

    setUploadPhase("finalizing");
    setUploadPercent(100);
    const completeData = await completeProductAttachment(
      user?.token,
      product._id,
      presignData.attachmentId
    );
    resetUploadUi();
    if (completeData?.type !== "success") {
      badToast(completeData?.message ?? "Falha ao finalizar upload");
      return;
    }
    goodToast("Anexo enviado!");
    setUploadName("");
    await refreshAfterMutation();
  };

  const openRename = (attachment) => {
    setRenamingAttachment(attachment);
    setRenameValue(attachment.name ?? "");
    setRenameSheetOpen(true);
  };

  const submitRename = async () => {
    if (!renamingAttachment || !product?._id) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renamingAttachment.name) {
      setRenameSheetOpen(false);
      return;
    }
    setRenameBusy(true);
    const data = await renameProductAttachment(
      user?.token,
      product._id,
      renamingAttachment._id,
      trimmed
    );
    setRenameBusy(false);
    if (data?.type !== "success") {
      badToast(data?.message ?? "Erro ao renomear anexo");
      return;
    }
    goodToast("Anexo renomeado!");
    setRenameSheetOpen(false);
    setRenamingAttachment(null);
    await refreshAfterMutation();
  };

  const confirmDeleteAttachment = async () => {
    if (!deleteAttachmentId || !product?._id) return;
    const data = await deleteProductAttachment(
      user?.token,
      product._id,
      deleteAttachmentId
    );
    setDeleteAttachmentId(null);
    if (data?.type !== "success") {
      badToast(data?.message ?? "Erro ao excluir anexo");
      return;
    }
    goodToast("Anexo excluído!");
    await refreshAfterMutation();
  };

  const confirmDeleteSerial = async () => {
    if (!deleteSerialTarget) return;
    const data = await deleteSerial(
      user?.token,
      deleteSerialTarget._id,
      deleteSerialTarget.isDraft
    );
    setDeleteSerialTarget(null);
    if (data?.type !== "success") {
      badToast("Erro ao excluir serial");
      return;
    }
    goodToast("Serial excluído!");
    await refreshAfterMutation();
  };

  const confirmRestoreHistory = async () => {
    if (!restoreHistoryEntry) return;
    setRestoreBusy(true);
    const his = restoreHistoryEntry;
    const body = {
      gradingType: his?.gradingType,
      numberOfCards: his?.numberOfCards,
      priority: his?.priority,
      deadline: his?.deadline,
      delivery: his?.delivery,
      finalPrice: his?.finalPrice,
      cardsValue: his?.cardsValue,
      insurance: his?.insurance,
      shipping: his?.shipping,
      shippingService: his?.shippingService,
      store: his?.store,
      postalCode: his?.postalCode,
      street: his?.street,
      district: his?.district,
      city: his?.city,
      state: his?.state,
      country: his?.country,
      houseNumber: his?.houseNumber,
      complement: his?.complement,
      status: his?.status,
    };
    const data = await updateProduct(user?.token, body, his?.product);
    setRestoreBusy(false);
    setRestoreHistoryEntry(null);
    if (data?.type === "success") {
      goodToast("Versão restaurada!");
      await refreshAfterMutation();
      return;
    }
    badToast("Erro ao restaurar versão");
  };

  if (loading) {
    return (
      <MobilePage title="Pedido">
        <div aria-label="Carregando pedido" role="status">
          <MobileSkeleton lines={6} />
        </div>
      </MobilePage>
    );
  }

  if (error || !product) {
    return (
      <MobilePage title="Pedido" onBack={() => navigate("/manafix/products")}>
        <MobileError
          message="Erro ao obter dados do pedido."
          onRetry={loadProduct}
        />
      </MobilePage>
    );
  }

  const userData = product.user;
  const productComments = product.comments ?? [];
  const userComments = userData?.comments ?? [];
  const attachments = product.attachments ?? [];

  return (
    <MobilePage title="Pedido" onBack={() => navigate("/manafix/products")}>
      <MobileSection title="Status">
        <p className="text-lg font-semibold text-laranjinha">
          {statusObject[product.status] ?? product.status}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setStatusSheetOpen(true)}
            className={actionBtnClass}
          >
            Alterar status
          </button>
          <button
            type="button"
            onClick={() => setAttachSheetOpen(true)}
            disabled={uploadBusy}
            className={actionBtnClass}
          >
            {uploadBusy ? "Enviando…" : "Adicionar anexo"}
          </button>
        </div>
      </MobileSection>

      <MobileSection title="Usuário">
        <DetailRow label="Nome" value={userData?.name} />
        <DetailRow label="E-mail" value={userData?.email} />
        <DetailRow label="Telefone" value={phoneMask(userData?.phone)} />
        <DetailRow label="CPF" value={cpfMask(userData?.documentNumber)} />
        <DetailRow label="Nascimento" value={formatarData(userData?.birth)} />
        {userData?._id ? (
          <button
            type="button"
            onClick={() => navigate(`/manafix/users/${userData._id}`)}
            className="mt-2 inline-flex min-h-[44px] items-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
          >
            Ver perfil do usuário
          </button>
        ) : null}
      </MobileSection>

      <MobileSection title="Pedido">
        <DetailRow label="Tipo" value={getGradingTypeLabel(product.gradingType)} />
        <DetailRow
          label="Autógrafo"
          value={product.autographEvaluation ? "Sim" : "Não"}
        />
        <DetailRow label="Nº de itens" value={String(product.numberOfCards)} />
        <DetailRow label="Prioridade" value={priorityTypes[product.priority]} />
        <DetailRow label="Prazo" value={product.deadline} />
        {product.receivedAt ? (
          <DetailRow
            label="Data prazo"
            value={formatarData(product.calculatedDeadline)}
          />
        ) : null}
        <DetailRow label="Entrega" value={product.delivery} />
        <DetailRow label="Preço final" value={cashnator(product.finalPrice)} />
        {product.declaredValueAllocationMode === "proportional_cards"
          || typeof product.declaredValueOrderTotalCents === "number"
          ? null
          : (
            <DetailRow label="Valor itens" value={cashnator(product.cardsValue)} />
          )}
        {product.checkoutSessionId || product.asaasPaymentId
          ? null
          : (
            <DetailRow label="Taxa de manuseio" value={cashnator(product.insurance)} />
          )}
        {product.delivery === "correios" ? (
          <>
            <DetailRow
              label="Valor frete"
              value={
                formatProductShipping(product.shipping, {
                  checkoutSessionId: product.checkoutSessionId,
                }) || "—"
              }
            />
            {(() => {
              const sharedNote = shippingSharedLabel({
                shippingShared:
                  product.shippingShared === true || product.checkoutItemCount > 1,
                checkoutItemCount: product.checkoutItemCount,
                shippingChargedOnThisProduct: product.shippingChargedOnThisProduct,
              });
              return sharedNote ? (
                <p className="text-xs text-primary px-1 -mt-1 mb-2">{sharedNote}</p>
              ) : null;
            })()}
            <DetailRow label="Tipo frete" value={product.shippingService} />
            <DetailRow label="Rastreio" value={product.trackingCode} />
          </>
        ) : null}
        <DetailRow label="Criado em" value={formatarData(product.createdAt)} />
        {product.paidAt ? (
          <DetailRow label="Pago em" value={formatarData(product.paidAt)} />
        ) : null}
        {product.store?.name ? (
          <DetailRow label="Loja" value={product.store.name} />
        ) : null}
        {product.returnTrackingCode ? (
          <DetailRow label="Rastreio retorno" value={product.returnTrackingCode} />
        ) : null}
        <button
          type="button"
          onClick={() => setEditSheetOpen(true)}
          className={`mt-2 ${actionBtnClass}`}
        >
          Editar pedido
        </button>
        <button
          type="button"
          onClick={() =>
            navigate("/manafix/serials/tags", {
              state: { origin: "products", products: [product._id] },
            })
          }
          className={`mt-2 ${primaryBtnClass}`}
        >
          Gerar etiquetas
        </button>
      </MobileSection>

      {product.checkoutSessionId || product.asaasPaymentId ? (
        <CheckoutGroupPanel
          variant="mobile"
          token={user?.token}
          productId={product._id}
          productStatus={product.status}
          checkoutSessionId={product.checkoutSessionId}
          asaasPaymentId={product.asaasPaymentId}
          productShipping={product.shipping}
          productInsurance={product.insurance}
          shippingShared={product.shippingShared}
          checkoutItemCount={product.checkoutItemCount}
          shippingChargedOnThisProduct={product.shippingChargedOnThisProduct}
          declaredValueOrderTotalCents={product.declaredValueOrderTotalCents}
          declaredValueAllocationMode={product.declaredValueAllocationMode}
          userEmail={user?.email}
          onStatusUpdated={refreshAfterMutation}
        />
      ) : null}

      <MobileSection title="Seriais">
        {serials.length === 0 ? (
          <MobileEmpty
            title="Sem seriais"
            message="Nenhum serial cadastrado para este pedido."
          />
        ) : (
          <ul className="space-y-2">
            {serials.map((serial) => {
              const visibleGrades = getVisibleGradeFields(serial, product);
              return (
                <li
                  key={serial._id}
                  className="rounded-lg border border-primary/30 px-3 py-2 text-sm text-laranjinha"
                >
                  <p className="font-medium">
                    {serial.serialNumber ?? serial._id}
                    {serial.isDraft ? (
                      <span className="ml-2 text-xs text-primary">(rascunho)</span>
                    ) : null}
                  </p>
                  {serial.gradableModel?.name ? (
                    <p className="text-xs text-laranjinha/60">
                      {serial.gradableModel.name} {serial.gradableModel.numbering}
                    </p>
                  ) : null}
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-laranjinha/70">
                    {visibleGrades.generalScore ? (
                      <span>
                        {visibleGrades.generalScore.label}:{" "}
                        {renderApplicableGradeValue(visibleGrades.generalScore.value)}
                      </span>
                    ) : null}
                    {visibleGrades.subgrades.map((field) => (
                      <span key={field.key}>
                        {field.label}: {renderApplicableGradeValue(field.value)}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSerial(serial);
                        setSerialEditOpen(true);
                      }}
                      className={actionBtnClass}
                    >
                      Editar notas
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteSerialTarget(serial)}
                      className="inline-flex min-h-[36px] items-center rounded-lg border border-red-700/60 px-3 text-xs font-medium text-red-400 hover:bg-red-950/40"
                    >
                      Excluir
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-2 text-xs text-laranjinha/60">
          Cadastrados: {serials.length}/{product.numberOfCards}
        </p>
        {serials.length < product.numberOfCards ? (
          <button
            type="button"
            onClick={() => setSerialCreateOpen(true)}
            className={`mt-2 ${primaryBtnClass}`}
          >
            Cadastrar serial
          </button>
        ) : null}
      </MobileSection>

      <MobileSection title="Comentários">
        <p className="mb-2 text-xs font-medium uppercase text-laranjinha/60">Do pedido</p>
        {productComments.length === 0 ? (
          <p className="text-sm text-laranjinha/60">Nenhum comentário</p>
        ) : (
          <ul className="mb-4 space-y-2">
            {productComments.map((comment, index) => (
              <li
                key={index}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm"
              >
                <p className="text-xs text-laranjinha/60">
                  {formatarDataCompleta(comment?.date)}
                </p>
                <p className="text-laranjinha">{comment?.text}</p>
              </li>
            ))}
          </ul>
        )}
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => {
              if (e.target.value.length <= 300) setNewComment(e.target.value);
            }}
            placeholder="Novo comentário"
            aria-label="Novo comentário"
            className="min-h-[44px] flex-1 rounded-lg border border-primary/50 px-3 text-sm"
          />
          <button
            type="button"
            disabled={!newComment.trim() || commentBusy}
            onClick={submitComment}
            className={primaryBtnClass}
          >
            Comentar
          </button>
        </div>
        <p className="mb-2 text-xs font-medium uppercase text-laranjinha/60">Do usuário</p>
        {userComments.length === 0 ? (
          <p className="text-sm text-laranjinha/60">Nenhum comentário</p>
        ) : (
          <ul className="space-y-2">
            {userComments.map((comment, index) => (
              <li
                key={index}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm"
              >
                <p className="text-xs text-laranjinha/60">
                  {formatarDataCompleta(comment?.date)}
                </p>
                <p className="text-laranjinha">{comment?.text}</p>
              </li>
            ))}
          </ul>
        )}
      </MobileSection>

      <MobileSection title="Anexos">
        {attachments.length === 0 ? (
          <MobileEmpty title="Sem anexos" message="Nenhum arquivo anexado." />
        ) : (
          <ul className="space-y-2">
            {attachments.map((attachment) => (
              <li
                key={attachment._id}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm"
              >
                <p className="font-medium text-laranjinha">{attachment.name}</p>
                {attachment.size ? (
                  <p className="text-xs text-laranjinha/60">{formatBytes(attachment.size)}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    disabled={downloadBusy === attachment._id}
                    aria-label={`Baixar ${attachment.name}`}
                    className={primaryBtnClass}
                  >
                    Baixar
                  </button>
                  <button
                    type="button"
                    onClick={() => openRename(attachment)}
                    className={actionBtnClass}
                  >
                    Renomear
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteAttachmentId(attachment._id)}
                    className="inline-flex min-h-[36px] items-center rounded-lg border border-red-700/60 px-3 text-xs font-medium text-red-400 hover:bg-red-950/40"
                  >
                    Excluir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 space-y-2">
          <input
            ref={galleryInputRef}
            type="file"
            className="hidden"
            disabled={uploadBusy}
            onChange={handleFilePick}
            data-testid="attach-input-gallery"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            disabled={uploadBusy}
            onChange={handleFilePick}
            data-testid="attach-input-camera"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            className="hidden"
            disabled={uploadBusy}
            onChange={handleFilePick}
            data-testid="attach-input-video"
          />
          <MobileFormField label="Nome do anexo (opcional)">
            <input
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="Usa nome do arquivo se vazio"
              aria-label="Nome do anexo"
              className="w-full rounded-lg border border-primary/50 bg-monao px-3 py-2 text-sm text-laranjinha"
            />
          </MobileFormField>
          <button
            type="button"
            disabled={uploadBusy}
            onClick={() => setAttachSheetOpen(true)}
            className={`w-full ${primaryBtnClass} min-h-[44px]`}
          >
            {uploadBusy ? "Enviando…" : "Adicionar anexo"}
          </button>
        </div>
      </MobileSection>

      <MobileFilterSheet
        open={attachSheetOpen}
        onClose={() => setAttachSheetOpen(false)}
        title="Adicionar anexo"
      >
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={uploadBusy}
            onClick={() => openAttachSource("gallery")}
            className={`w-full ${actionBtnClass} min-h-[44px] justify-center`}
          >
            Galeria / arquivo
          </button>
          <button
            type="button"
            disabled={uploadBusy}
            onClick={() => openAttachSource("camera")}
            className={`w-full ${primaryBtnClass} min-h-[44px] justify-center`}
          >
            Tirar foto
          </button>
          <button
            type="button"
            disabled={uploadBusy}
            onClick={() => openAttachSource("video")}
            className={`w-full ${primaryBtnClass} min-h-[44px] justify-center`}
          >
            Gravar vídeo
          </button>
        </div>
      </MobileFilterSheet>

      <MobileSection title="Histórico">
        {history.length === 0 ? (
          <MobileEmpty title="Sem histórico" message="Nenhuma alteração registrada." />
        ) : (
          <ul className="space-y-3">
            {history.map((entry) => (
              <li
                key={entry._id}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm"
              >
                <p className="text-xs text-laranjinha/60">
                  {formatarDataCompleta(entry.createdAt)}
                </p>
                <p className="text-laranjinha">
                  Status: {statusObject[entry.status] ?? entry.status}
                </p>
                {entry.alteredBy?.email ? (
                  <p className="text-xs text-laranjinha/60">
                    Por {entry.alteredBy.email}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => setRestoreHistoryEntry(entry)}
                  className={`mt-2 ${actionBtnClass}`}
                >
                  Restaurar versão
                </button>
              </li>
            ))}
          </ul>
        )}
      </MobileSection>

      <MobileProductStatusSheet
        open={statusSheetOpen}
        onClose={() => setStatusSheetOpen(false)}
        product={product}
        user={user}
        serials={serials}
        onSuccess={refreshAfterMutation}
        goodToast={goodToast}
        badToast={badToast}
      />
      <MobileProductEditSheet
        open={editSheetOpen}
        onClose={() => setEditSheetOpen(false)}
        product={product}
        user={user}
        onSuccess={refreshAfterMutation}
        goodToast={goodToast}
        badToast={badToast}
      />
      <MobileSerialCreateSheet
        open={serialCreateOpen}
        onClose={() => setSerialCreateOpen(false)}
        product={product}
        user={user}
        onSuccess={refreshAfterMutation}
        goodToast={goodToast}
        badToast={badToast}
      />
      <MobileSerialEditSheet
        open={serialEditOpen}
        onClose={() => {
          setSerialEditOpen(false);
          setEditingSerial(null);
        }}
        serial={editingSerial}
        product={product}
        user={user}
        onSuccess={refreshAfterMutation}
        goodToast={goodToast}
        badToast={badToast}
      />

      <MobileFilterSheet
        open={renameSheetOpen}
        onClose={() => setRenameSheetOpen(false)}
        title="Renomear anexo"
      >
        <MobileFormField label="Novo nome">
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-label="Novo nome do anexo"
            className="w-full rounded-lg border border-primary/50 bg-monao px-3 py-2 text-sm text-laranjinha"
          />
        </MobileFormField>
        <button
          type="button"
          disabled={renameBusy}
          onClick={submitRename}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
        >
          {renameBusy ? "Salvando…" : "Salvar nome"}
        </button>
      </MobileFilterSheet>

      <MobileConfirm
        open={Boolean(deleteAttachmentId)}
        title="Excluir anexo"
        message="Excluir este anexo? Esta ação não pode ser desfeita."
        onConfirm={confirmDeleteAttachment}
        onCancel={() => setDeleteAttachmentId(null)}
      />
      <MobileConfirm
        open={Boolean(deleteSerialTarget)}
        title="Excluir serial"
        message="Excluir este serial?"
        onConfirm={confirmDeleteSerial}
        onCancel={() => setDeleteSerialTarget(null)}
      />
      <MobileConfirm
        open={Boolean(restoreHistoryEntry)}
        title="Restaurar versão"
        message="Restaurar o pedido para esta versão do histórico?"
        onConfirm={confirmRestoreHistory}
        onCancel={() => setRestoreHistoryEntry(null)}
      />
      {restoreBusy ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" role="status">
          <span className="rounded-lg bg-mgs-bg px-4 py-2 text-sm">Restaurando…</span>
        </div>
      ) : null}

      <MobileUploadOverlay
        open={uploadBusy}
        phase={uploadPhase}
        percent={uploadPercent}
        fileName={uploadFileName}
      />
    </MobilePage>
  );
}
